import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import GenerationForm from '../components/GenerationForm';
import { Button } from '../components/ui/button';
import { Zap, FileText, Download, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 grid-texture" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block mb-6 px-4 py-2 bg-gradient-to-r from-blue-600/20 to-violet-600/20 border border-blue-500/30 rounded-full"
            >
              <span className="text-sm font-medium text-blue-400">AI-Powered Test Case Generation</span>
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
              Generate Test Cases
              <br />
              <span className="text-blue-500">in Seconds</span>
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              Transform your requirements into comprehensive, import-ready test cases for Azure DevOps and JIRA. 
              Powered by advanced AI.
            </p>
          </motion.div>

          {/* Generation Form */}
          <GenerationForm />
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-card/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            <FeatureCard
              icon={<Zap className="h-8 w-8 text-blue-500" />}
              title="Lightning Fast"
              description="Generate comprehensive test cases in under 15 seconds"
            />
            <FeatureCard
              icon={<FileText className="h-8 w-8 text-violet-500" />}
              title="Multiple Platforms"
              description="Export to Azure DevOps and JIRA with proper formatting"
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8 text-green-500" />}
              title="Enterprise Ready"
              description="Secure, scalable, and built for professional teams"
            />
          </motion.div>
        </div>
      </section>

      {/* CTA - Hidden for now */}
      {/* <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-heading font-bold mb-6">Ready for Unlimited Access?</h2>
          <p className="text-muted-foreground mb-8">
            Upgrade to our paid plan for unlimited test case generation
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90 shadow-[0_0_30px_rgba(59,130,246,0.4)]"
            data-testid="view-pricing-button"
          >
            View Pricing
          </Button>
        </div>
      </section> */}

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">© 2025 MyTestCase. All rights reserved.</p>
            <div className="flex flex-wrap gap-6 justify-center">
              <button 
                onClick={() => navigate('/privacy-policy')} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="privacy-policy-link"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/about-us')} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="about-us-link"
              >
                About Us
              </button>
              <button 
                onClick={() => navigate('/contact-us')} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="contact-us-link"
              >
                Contact Us
              </button>
              <button 
                onClick={() => navigate('/terms-of-use')} 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="terms-of-use-link"
              >
                Terms of Use
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-card border border-border/50 hover:border-border transition-colors rounded-lg p-6"
    >
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-heading font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </motion.div>
  );
}